import React, { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import PasswordInput from "@/components/PasswordInput";
import { toast } from "sonner";
import { CheckCircle2, ArrowLeft } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) return toast.error("Password must be at least 6 characters");
    if (pw !== pw2) return toast.error("Passwords don't match");
    if (!token) return toast.error("Missing token — open the link from your email");
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password: pw });
      setDone(true);
      setTimeout(() => nav("/login"), 2200);
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-secondary/40 flex items-center justify-center px-4">
      <div className="w-full max-w-md bg-background border border-border rounded-sm p-8">
        <Link to="/login" className="inline-flex items-center text-xs text-muted-foreground hover:text-foreground mb-6">
          <ArrowLeft className="w-3 h-3 mr-1" /> Back to sign in
        </Link>
        <h1 className="font-display text-3xl font-black tracking-tighter">Set your password</h1>
        <p className="text-sm text-muted-foreground mt-2 mb-6">
          Choose a new password to {token ? "activate your account or reset access" : "continue"}.
        </p>
        {done ? (
          <div className="border border-success/40 bg-success/10 rounded-sm p-4 text-sm" data-testid="reset-done">
            <div className="flex items-center gap-2 font-semibold text-success">
              <CheckCircle2 className="w-4 h-4" /> Password updated
            </div>
            <p className="text-muted-foreground mt-1">Redirecting you to sign in…</p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label>New password</Label>
              <PasswordInput required value={pw} onChange={(e) => setPw(e.target.value)}
                     className="rounded-sm h-11" autoFocus minLength={6} inputTestId="reset-password-input" testId="reset-password-toggle" />
            </div>
            <div>
              <Label>Confirm password</Label>
              <PasswordInput required value={pw2} onChange={(e) => setPw2(e.target.value)}
                     className="rounded-sm h-11" minLength={6} inputTestId="reset-password-confirm-input" testId="reset-confirm-toggle" />
            </div>
            <Button type="submit" disabled={busy || !token} className="w-full rounded-sm h-11" data-testid="reset-submit-button">
              {busy ? "Saving…" : "Save password"}
            </Button>
            {!token && <p className="text-xs text-destructive">No reset token in the URL. Open the link from your email.</p>}
          </form>
        )}
      </div>
    </div>
  );
}
